import { useMemo, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { User } from "@/types";

/**
 * Responsible-person (vastuuhenkilö) picker: the project's people as toggle
 * buttons. A single person can be responsible, so selecting one greys out the
 * others (they stay clickable to switch). Tapping the selected button clears
 * the selection. No one is selected by default.
 *
 * When `people` is empty, `emptyMessage` is shown instead of buttons.
 */

/** Full name, falling back to email when names are missing. */
function fullName(user: User): string {
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  return user.firstName || user.lastName || user.email;
}

/**
 * Button label per person: first name, but disambiguated with the last
 * initial when two people in the set share a first name.
 */
function buildLabels(people: User[]): Map<string, string> {
  const firstNameCounts = new Map<string, number>();
  for (const p of people) {
    const fn = p.firstName || "";
    firstNameCounts.set(fn, (firstNameCounts.get(fn) || 0) + 1);
  }
  const labels = new Map<string, string>();
  for (const p of people) {
    const fn = p.firstName;
    if (!fn) {
      labels.set(p.id, p.lastName || p.email);
      continue;
    }
    const ambiguous = (firstNameCounts.get(fn) || 0) > 1;
    labels.set(p.id, ambiguous && p.lastName ? `${fn} ${p.lastName[0]}.` : fn);
  }
  return labels;
}

export function ResponsiblePeoplePicker({
  people,
  value,
  onChange,
  emptyMessage,
}: {
  people: User[];
  value: string;
  onChange: (userId: string) => void;
  emptyMessage: ReactNode;
}) {
  const labels = useMemo(() => buildLabels(people), [people]);

  if (people.length === 0) {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 p-3 text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  const hasSelection = !!value;

  return (
    <div className="flex flex-wrap gap-2">
      {people.map((person) => {
        const isSelected = person.id === value;
        return (
          <button
            key={person.id}
            type="button"
            title={fullName(person)}
            onClick={() => onChange(isSelected ? "" : person.id)}
            className={cn(
              "rounded-md border px-4 py-2.5 text-sm font-normal transition-colors",
              isSelected
                ? "border-primary bg-primary text-primary-foreground"
                : hasSelection
                  ? "border-input bg-background text-muted-foreground opacity-60 hover:opacity-100 hover:bg-accent hover:text-accent-foreground"
                  : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
            )}
          >
            {labels.get(person.id)}
          </button>
        );
      })}
    </div>
  );
}
